import { Button } from '@material-ui/core';
import { CircularProgress, Grid, Paper } from '@material-ui/core';
import axios from 'axios';
import Alert from 'components/Alert';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_MYEDUZZ } from 'settings';
import GlobalLoading from 'v2/components/Shared/GlobalLoading';
import NoDangerouslyHTML from 'v2/components/Shared/NoDangerouslyHTML';
import PublicLayout from 'v2/components/Shared/PublicLayout';
import Truncate from 'v2/components/Shared/TruncateText';
import { formatterApiErrors } from 'v2/formatters/apiErrors';
import { formatMoney, formatPercentual } from 'v2/helpers/money';
import useGetUser from 'v2/hooks/useGetUser';
import affiliateService from 'v2/services/affiliate';

import { useStyle } from './styles';

interface IProduct {
  id: number;
  title: string;
  description: string;
  producer_name: string;
  showcase_commission: string;
  image: string;
  recruiter_name: string;
  recruiter_id: number;
  commission_type: string;
  commission_value: number;
}

const InviteAffiliate = () => {
  const classes = useStyle();
  const [product, setProduct] = useState<IProduct>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { productId, affiliateId } = useParams<{ productId: string; affiliateId?: string }>();

  const user = useGetUser();

  useEffect(() => {
    if (productId) {
      let url = `${API_MYEDUZZ}/showcase/public/${productId}`;
      if (affiliateId) url = `${url}?recruiter=${affiliateId}`;
      axios
        .get(url)
        .then((res) => {
          setProduct(res.data.data);
          setIsLoading(false);
        })
        .catch((e) => {
          toast.error(formatterApiErrors(e), { autoClose: false });
        });
    }
  }, [affiliateId, productId]);

  const affiliateCommissionForSale = useMemo(() => {
    if (!product?.commission_type || !product?.commission_value) return '';
    const isPercentual = product.commission_type === 'P';

    const formmater = isPercentual ? formatPercentual : formatMoney;
    const commission = formmater(product.commission_value);

    return `até ${commission}`;
  }, [product]);

  if (isLoading) return <GlobalLoading />;

  return (
    <PublicLayout>
      <Grid container className={classes.container}>
        <Grid item xs={12}>
          <h4>
            Você foi convidado por {product.recruiter_name !== '' ? product.recruiter_name : product.producer_name} a se tornar afiliado do produto
            abaixo:
          </h4>
        </Grid>
      </Grid>
      <Paper elevation={4} className={classes.defaultPadding}>
        <Grid container spacing={2} justify='flex-start' alignItems='center'>
          <Grid item xs={4}>
            <Paper elevation={0} className={classes.paperImage}>
              <img className={classes.image} src={product.image} alt='Preview' />
            </Paper>
          </Grid>
          <Grid item xs={8}>
            <div className={classes.title}>{product.title}</div>
            <div className={classes.subtitle2}>
              Você receberá <b>{affiliateCommissionForSale}</b> por venda
            </div>
          </Grid>
          <Grid item xs={12}>
            <div className={classes.description}>
              <Truncate final='...' count={1090}>
                <NoDangerouslyHTML html={product.description} />
              </Truncate>
            </div>
          </Grid>
          {user ? <Authenticated product={product} /> : <NoAuthenticated />}
        </Grid>
      </Paper>
      <Grid container className={classes.container}>
        <Grid item xs={12}>
          <h4>
            Você receberá um link e poderá ganhar dinheiro vendendo este produto em sites, blogs, redes sociais e e-mail.{' '}
            <a target='_blank' rel='noopener noreferrer' href='https://ajuda.eduzz.com/?article=primeiro-passos-de-um-afiliado'>
              Saiba Mais
            </a>
          </h4>
        </Grid>
      </Grid>
    </PublicLayout>
  );
};

const NoAuthenticated = () => {
  const { productId, affiliateId } = useParams<{ productId: string; affiliateId?: string }>();
  let url = `/affiliate/invite/${productId}`;
  if (affiliateId) url = `${url}/${affiliateId}`;
  return (
    <Grid item xs={12}>
      <Grid container spacing={2} direction='row' justify='space-between' alignItems='center'>
        <Grid item xs={12} md={6}>
          <Link to={`${url}?login=true&register=true`}>
            <Button variant='contained' color='primary' fullWidth>
              Fazer meu cadastro
            </Button>
          </Link>
        </Grid>
        <Grid item xs={12} md={6}>
          <Link to={`${url}?login=true`}>
            <Button variant='contained' color='secondary' fullWidth>
              Já tenho cadastro
            </Button>
          </Link>
        </Grid>
      </Grid>
    </Grid>
  );
};

const Authenticated = ({ product }: { product: IProduct }) => {
  const [done, setDone] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const user = useGetUser();
  const openModal = async () => {
    const confirm = await Alert.confirm({
      message: <div style={{ fontSize: '1.0rem' }}>Você deseja realmente solicitar afiliação desse produto?</div>,
      title: <>Solicitar afiliação</>,
      buttonOk: <>AFILIAR-SE</>,
      buttonCancel: <>Fechar</>,
      confirmation: true,
    });

    if (confirm) {
      setSending(true);
      const fetchData = async () => {
        try {
          affiliateService.inviteAffiliate(product.id, product.recruiter_id, user.id).subscribe(
            () => {
              setDone(true);
              setSending(false);
              toast.success('Afiliação solicitada com sucesso, agora você pode acompanhar o status da sua solicitação em minhas afiliações');
            },
            (err) => {
              setSending(false);
              toast.error(err);
            }
          );
        } catch (error) {
          setSending(false);
          toast.error(error.message);
        }
      };

      return fetchData();
    }
  };

  if (done) {
    return (
      <Grid item xs={12}>
        <Grid container spacing={2} direction='row' justify='flex-end' alignItems='center'>
          <Grid item>
            <Link to='/producer/affiliations'>
              <Button variant='contained' color='primary'>
                Ir para minhas afiliações
              </Button>
            </Link>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Grid item xs={12}>
        <Grid container spacing={2} direction='row' justify='flex-end' alignItems='center'>
          <Grid item>
            <Button variant='contained' color='secondary' onClick={openModal} disabled={sending}>
              {sending && <CircularProgress size={12} style={{ marginRight: '0.5rem' }} />}
              Afiliar-se
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default InviteAffiliate;
